import { lazy } from 'react';
import { z } from 'zod';
import { useMetricLatestMany } from '../_sdk/hooks';
import { decimalsSchema, unitSchema } from '../_sdk/schema';
import { defineWidget, type WidgetDataState } from '../_sdk/types';

/**
 * Map (PRD VS-042): OpenStreetMap tiles and up to 20 named markers; a marker bound
 * to a metric shows its newest value. Bound by props, never to a source type.
 */

const MarkerSchema = z.object({
  label: z.string().trim().min(1).max(80),
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
  binding: z
    .object({
      sourceId: z.uuid(),
      metricName: z.string().min(1).max(200),
      /** One series: the newest value of its first match is shown. */
      tags: z.record(z.string(), z.string()).default({}),
      unit: unitSchema,
      decimals: decimalsSchema,
    })
    .optional(),
});

export const MAX_MARKERS = 20;

export const PropsSchema = z.object({
  label: z.string().max(80).optional(),
  center: z.object({ lat: z.number().min(-90).max(90), lon: z.number().min(-180).max(180) }),
  zoom: z.number().int().min(3).max(18).default(12),
  markers: z.array(MarkerSchema).max(MAX_MARKERS).default([]),
});

export type MapProps = z.output<typeof PropsSchema>;
export type MapMarkerProps = MapProps['markers'][number];

/** `undefined` while loading, `null` when the metric has no value (or failed). */
export type Reading = { value: number; ts: string } | null | undefined;
export type MapData = { marker: MapMarkerProps; reading: Reading }[];

/** Bordeaux: where Hugo lives; any centre can be set in the editor. */
export const DEFAULT_CENTER = { lat: 44.8378, lon: -0.5792 };

function useMapData(p: MapProps): WidgetDataState<MapData> {
  const bound = p.markers.flatMap((m) =>
    m.binding
      ? [{ sourceId: m.binding.sourceId, name: m.binding.metricName, tags: m.binding.tags }]
      : [],
  );
  const queries = useMetricLatestMany(bound);
  let q = 0;
  const data = p.markers.map((marker) => {
    if (!marker.binding) return { marker, reading: null };
    const query = queries[q++];
    const item = query?.data?.items[0];
    const reading: Reading = item
      ? { value: item.value, ts: item.ts }
      : query?.isPending && !query.isError
        ? undefined
        : null;
    return { marker, reading };
  });
  const newest = Math.max(0, ...data.map((d) => (d.reading ? Date.parse(d.reading.ts) : 0)));
  // The map itself never waits: tiles and markers show at once, values fill in.
  return {
    status: 'ready',
    data,
    retry: () => {
      for (const query of queries) void query.refetch();
    },
    ...(newest > 0 ? { lastUpdated: new Date(newest) } : {}),
  };
}

export default defineWidget<MapProps, MapData>({
  type: 'map',
  category: 'spatial',
  defaultSize: { w: 6, h: 4 },
  minSize: { w: 3, h: 3 },
  propsSchema: PropsSchema,
  defaults: { center: DEFAULT_CENTER, zoom: 12, markers: [] },
  sourceIds: (p) => [...new Set(p.markers.flatMap((m) => (m.binding ? [m.binding.sourceId] : [])))],
  useData: useMapData,
  Render: lazy(() => import('./render')),
  Editor: lazy(() => import('./editor')),
});
